/* Workspace Org detail — Owner/Revisor basic stats view OR Admin/Moderator scoped list */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, Download, CornerDownRight, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { API } from '../../utils/api';
import { RoleChip, wsErr, downloadCsv } from './workspaceUi';
import { fmtKr } from '../owner/ownerUi';

function rangeDates(key) {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (key === '7d' ? 7 : key === '90d' ? 90 : 30));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function WsOrg({ org, t, lang, headers, onOpenSub }) {
  const roles = useMemo(() => new Set((org.roles || []).map((r) => r.role)), [org]);
  const isOwnerish = org.is_owner || roles.has('revisor');
  const hasMgmt = roles.has('admin') || roles.has('moderator');

  return (
    <div className="space-y-5">
      {isOwnerish && <StatsView org={org} t={t} lang={lang} headers={headers} canExport={roles.has('revisor') || org.is_owner} isOwner={org.is_owner} />}
      {hasMgmt && <ScopeList org={org} t={t} headers={headers} onOpenSub={onOpenSub} />}
      {!isOwnerish && !hasMgmt && <p className="text-[13px] text-slate-400 py-8 text-center">{t.no_data}</p>}
    </div>
  );
}

/* -------- Owner / Revisor: basic live stats with filters (view-only) -------- */

function StatsView({ org, t, lang, headers, canExport, isOwner }) {
  const [rangeKey, setRangeKey] = useState('30d');
  const [division, setDivision] = useState('all');
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);
  const range = useMemo(() => rangeDates(rangeKey), [rangeKey]);

  const load = useCallback(async () => {
    try {
      setErr(false);
      const params = { from: range.from, to: range.to };
      if (division !== 'all') params.division = division;
      const res = await axios.get(`${API}/org/${org.org_id}/stats`, { headers, params });
      setStats(res.data);
    } catch (e) { console.error(e); setErr(true); }
  }, [org.org_id, headers, range.from, range.to, division]);
  useEffect(() => { load(); }, [load]);

  const [divOptions, setDivOptions] = useState([]);
  useEffect(() => {
    axios.get(`${API}/org/${org.org_id}/scope/sub-accounts`, { headers })
      .then((r) => setDivOptions(r.data.items || [])).catch(() => {});
  }, [org.org_id, headers]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/org/${org.org_id}/scope/export`,
        { headers, params: { from: range.from, to: range.to } });
      downloadCsv(res.data.csv_data, `paymig-org-${range.from}-${range.to}.csv`);
    } catch (e) { toast.error(wsErr(e)); }
  };

  if (err) return <p className="text-sm text-rose-600">{t.error_load}</p>;

  const k = stats?.kpis;
  const maxDiv = Math.max(1, ...(stats?.by_division || []).map((d) => d.value));

  return (
    <section data-testid="ws-stats-view" className="space-y-3">
      <div className="flex gap-2">
        <Select value={rangeKey} onValueChange={setRangeKey}>
          <SelectTrigger data-testid="ws-stats-range" className="h-9 flex-1 text-[13px] bg-white border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">{t.range_7d}</SelectItem>
            <SelectItem value="30d">{t.range_30d}</SelectItem>
            <SelectItem value="90d">{t.range_90d}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={division} onValueChange={setDivision}>
          <SelectTrigger data-testid="ws-stats-division" className="h-9 flex-1 text-[13px] bg-white border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.all_divisions}</SelectItem>
            {divOptions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!stats ? (
        <p className="text-[13px] text-slate-400 py-6 text-center">{t.loading}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            {[
              ['ws-kpi-collected', t.kpi_collected, fmtKr(k.total_collected.value)],
              ['ws-kpi-tx', t.kpi_tx, new Intl.NumberFormat('sv-SE').format(k.transactions.value)],
              ['ws-kpi-avg', t.kpi_avg, fmtKr(k.avg_payment.value)],
              ['ws-kpi-payers', t.kpi_payers, new Intl.NumberFormat('sv-SE').format(k.unique_payers.value)],
            ].map(([tid, label, value]) => (
              <div key={tid} data-testid={tid} className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
                <div className="text-[11.5px] text-slate-500">{label}</div>
                <div className="text-[19px] font-semibold tabular-nums mt-0.5">{value}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="ws-by-division">
            <h3 className="text-[13px] font-semibold mb-3">{t.by_division}</h3>
            {(stats.by_division || []).length === 0 ? (
              <p className="text-[12.5px] text-slate-400 py-4 text-center">{t.no_data}</p>
            ) : (
              <div className="space-y-2.5">
                {stats.by_division.map((d) => (
                  <div key={d.sub_account_id || d.name}>
                    <div className="flex justify-between text-[12.5px] mb-1">
                      <span className="text-slate-600 truncate">{d.name}</span>
                      <span className="font-semibold tabular-nums shrink-0">{fmtKr(d.value)}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-teal-600" style={{ width: `${Math.round((d.value / maxDiv) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex gap-2">
        {canExport && (
          <Button variant="outline" data-testid="ws-org-export" className="flex-1 h-9 text-[13px] border-slate-200" onClick={exportCsv}>
            <Download size={14} className="mr-1.5" />{t.export_csv}
          </Button>
        )}
        {isOwner && (
          <Button variant="outline" data-testid="ws-open-erp" className="flex-1 h-9 text-[13px] border-slate-200"
                  onClick={() => { window.location.href = '/owner'; }}>
            <ExternalLink size={14} className="mr-1.5" />{t.open_erp}
          </Button>
        )}
      </div>
      <p className="text-[11.5px] text-slate-400 text-center">{t.view_only}</p>
    </section>
  );
}

/* -------- Admin / Moderator: scoped divisions & events tree -------- */

function ScopeList({ org, t, headers, onOpenSub }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/org/${org.org_id}/scope/sub-accounts`, { headers })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { console.error(e); setErr(true); });
  }, [org.org_id, headers]);

  if (err) return <p className="text-sm text-rose-600">{t.error_load}</p>;
  if (!items) return <p className="text-[13px] text-slate-400 py-6 text-center">{t.loading}</p>;

  const managed = items.filter((i) => i.my_roles.includes('admin') || i.my_roles.includes('moderator'));
  const ids = new Set(managed.map((i) => i.id));
  const roots = managed.filter((i) => !i.parent_sub_account_id || !ids.has(i.parent_sub_account_id));
  const childrenOf = (id) => managed.filter((i) => i.parent_sub_account_id === id);

  const Row = ({ item, depth }) => (
    <>
      <button data-testid={`ws-scope-item-${item.id}`} onClick={() => onOpenSub(item)}
              className="w-full bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm flex items-center gap-2.5 text-left hover:border-teal-300 transition-colors"
              style={{ marginLeft: depth * 16, width: `calc(100% - ${depth * 16}px)` }}>
        {depth > 0 && <CornerDownRight size={13} className="text-slate-300 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold truncate">{item.name}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className={`text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${item.type === 'DIVISION' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
              {item.type === 'DIVISION' ? t.division : t.event}
            </span>
            {item.my_roles.filter((r) => r === 'admin' || r === 'moderator').map((r) => <RoleChip key={r} role={r} t={t} />)}
          </div>
        </div>
        <div className="text-[12.5px] font-semibold text-teal-700 tabular-nums shrink-0">{fmtKr(item.total_received)}</div>
        <ChevronRight size={15} className="text-slate-300 shrink-0" />
      </button>
      {childrenOf(item.id).map((c) => <Row key={c.id} item={c} depth={depth + 1} />)}
    </>
  );

  return (
    <section data-testid="ws-scope-list">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400 mb-2">{t.my_scope}</h2>
      {roots.length === 0 ? (
        <p className="text-[13px] text-slate-400 py-6 text-center">{t.no_data}</p>
      ) : (
        <div className="space-y-2">{roots.map((r) => <Row key={r.id} item={r} depth={0} />)}</div>
      )}
    </section>
  );
}
